import DOMPurify from 'dompurify';

/**
 * Transforme le contenu texte en HTML enrichi avec des embeds de médias
 * Supporte: YouTube, Vimeo, Twitch, Streamable, Medal.tv, TikTok, Dailymotion, etc.
 * Gère aussi les images (Gyazo, Imgur, Discord) et fichiers audio/vidéo directs
 */
function formatHtmlContent(content) {
	const originalContent = content;

	// Validation du contenu
	if (!content || (typeof content !== 'string' && !(content instanceof String))) {
		return {
			textHasRawContent: true,
			textHasVideo: false,
			textHasImage: false,
			textHasLink: false,
			textHasSound: false,
			textHasYoutubeVideo: false,
			textHasEmbed: false,
			content: content || '',
			originalContent,
		};
	}

	// Nettoyage des balises chevrons doubles
	content = content.replace(/<</g, '').replace(/>>/g, '');

	// Sanitization pour éviter les injections XSS
	content = DOMPurify.sanitize(content);

	// ==========================================
	// PLAYER LINK CARDS - Liens vers profils joueurs
	// ==========================================
	
	// Détection des liens joueurs tasev3.bay.life/players/IDENTIFIER
	const playerLinkMatches = [];
	if (content.includes('tasev3.bay.life/players/')) {
		const playerLinkRegex = /(?:https?:\/\/)?tasev3\.bay\.life\/players\/([a-zA-Z0-9:_-]+)/g;
		let match;
		while ((match = playerLinkRegex.exec(content)) !== null) {
			playerLinkMatches.push({
				fullMatch: match[0],
				identifier: match[1],
				index: match.index
			});
		}
		
		// Remplacer par des placeholders React
		playerLinkMatches.forEach((linkMatch, index) => {
			const placeholder = `<div data-player-link="${linkMatch.identifier}" data-player-link-index="${index}"></div>`;
			content = content.replace(linkMatch.fullMatch, placeholder);
		});
	}

	// ==========================================
	// CONFIGURATION DES EMBEDS
	// ==========================================
	const EMBED_STYLES = {
		// Container responsive 16:9 optimisé pour les cards
		container: 'position: relative; width: 100%; max-width: 480px; min-height: 270px; aspect-ratio: 16/9; overflow: hidden; border-radius: 8px; margin: 8px 0;',
		iframe: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;',
		video: 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: contain;',
	};

	// ==========================================
	// VIDÉO EMBEDS - PLATEFORMES
	// ==========================================

	// YouTube - Supporte les formats: youtube.com/watch?v=, youtu.be/, youtube.com/live/, avec timestamp
	if (content.includes('youtube.com') || content.includes('youtu.be')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:\?[^\s<>"']+)?/g,
			(match, videoId) => {
				// Extraire le timestamp du match complet s'il existe
				const timestampMatch = match.match(/[?&](?:t|start)=(\d+)/);
				const timestamp = timestampMatch ? timestampMatch[1] : null;
				const startParam = timestamp ? `&start=${timestamp}` : '';
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://www.youtube.com/embed/${videoId}?autoplay=0${startParam}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Vimeo - Supporte: vimeo.com/123456 et player.vimeo.com/video/123456
	if (content.includes('vimeo.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?(\d+)(?:\?[^\s]*)?/g,
			(match, videoId) => {
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://player.vimeo.com/video/${videoId}" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Twitch Clips
	if (content.includes('clips.twitch.tv') || (content.includes('twitch.tv/') && content.includes('/clip/'))) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?(?:clips\.twitch\.tv\/([a-zA-Z0-9_-]+)|twitch\.tv\/\w+\/clip\/([a-zA-Z0-9_-]+))(?:\?[^\s]*)?/g,
			(match, clipId1, clipId2) => {
				const clipId = clipId1 || clipId2;
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://clips.twitch.tv/embed?clip=${clipId}&parent=${window.location.hostname}" frameborder="0" allowfullscreen scrolling="no" style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Streamable
	if (content.includes('streamable.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?streamable\.com\/([a-zA-Z0-9]+)(?:\?[^\s]*)?/g,
			(match, videoId) => {
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://streamable.com/e/${videoId}?loop=0" frameborder="0" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Medal.tv - Utilise l'iframe embed officiel
	// Supporte: medal.tv/clips/ID, medal.tv/games/GAME/clips/ID, medal.tv/fr/games/GAME/clips/ID (avec code langue)
	if (content.includes('medal.tv')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?medal\.tv\/(?:[a-z]{2}\/)?(?:games\/[^/]+\/)?clips?\/([a-zA-Z0-9_-]+)(?:\/[a-zA-Z0-9_-]+)?(?:\?[^\s]*)?/g,
			(match, clipId) => {
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://medal.tv/clip/${clipId}?autoplay=0&muted=1&loop=0" frameborder="0" allow="autoplay" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// TikTok - Embed standard
	if (content.includes('tiktok.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._-]+)\/video\/(\d+)(?:\?[^\s]*)?/g,
			(match, username, videoId) => {
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://www.tiktok.com/embed/v2/${videoId}" frameborder="0" allow="encrypted-media;" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Dailymotion
	if (content.includes('dailymotion.com') || content.includes('dai.ly')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?(?:dailymotion\.com\/video\/|dai\.ly\/)([a-zA-Z0-9]+)(?:\?[^\s]*)?/g,
			(match, videoId) => {
				return `<div style="${EMBED_STYLES.container}"><iframe src="https://www.dailymotion.com/embed/video/${videoId}" frameborder="0" allow="autoplay; fullscreen" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
			}
		);
	}

	// Kick.com clips
	if (content.includes('kick.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?kick\.com\/([a-zA-Z0-9_-]+)(?:\/clips\/([a-zA-Z0-9_-]+))?(?:\?[^\s]*)?/g,
			(match, channel, clipId) => {
				if (clipId) {
					return `<div style="${EMBED_STYLES.container}"><iframe src="https://player.kick.com/clips/${clipId}" frameborder="0" allowfullscreen style="${EMBED_STYLES.iframe}"></iframe></div>`;
				}
				return match; // Si c'est juste un lien vers une chaîne, on ne l'embed pas
			}
		);
	}

	// ==========================================
	// IMAGES - PLATEFORMES
	// ==========================================

	// Gyazo - Images directes (i.gyazo.com)
	if (content.includes('i.gyazo.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?i\.gyazo\.com\/([\w.-_]+)/g,
			(match, imageId) => {
				return `<img src="${match}" alt="Gyazo Image" style="max-width: 100%; height: auto; display: block; cursor: pointer;">`;
			}
		);
	}
	// Gyazo - Liens normaux (conversion en .png)
	else if (content.includes('gyazo.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?gyazo\.com\/([\w]+)(?!\.(?:png|jpg|jpeg|gif))/g,
			(match, imageId) => {
				const imageUrl = `https://i.gyazo.com/${imageId}.png`;
				return `<img src="${imageUrl}" alt="Gyazo Image" style="max-width: 100%; height: auto; display: block; cursor: pointer;">`;
			}
		);
	}

	// Imgur - Albums (lien simple sans embed)
	if (content.includes('imgur.com/a/')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?imgur\.com\/a\/([\w]+)/g,
			(match) => {
				return `<a href="${match}" target="_blank" rel="noopener noreferrer">${match}</a>`;
			}
		);
	}
	// Imgur - Images directes
	else if (content.includes('imgur.com') || content.includes('i.imgur.com')) {
		content = content.replace(
			/(?:https?:\/\/)?(?:www\.)?(?:i\.)?imgur\.com\/([\w]+)\.?(jpg|jpeg|png|gif)?/g,
			(match, imageId, ext) => {
				const extension = ext || 'jpg';
				const imageUrl = `https://i.imgur.com/${imageId}.${extension}`;
				return `<img src="${imageUrl}" alt="Imgur Image" style="max-width: 100%; height: auto; display: block; cursor: pointer;">`;
			}
		);
	}

  // Discord CDN - Attachments (supporte cdn.discordapp.com ET media.discordapp.net)
  if (content.includes('cdn.discordapp.com/attachments') || content.includes('media.discordapp.net/attachments')) {
    content = content.replace(
      /(https:\/\/(?:cdn\.discordapp\.com|media\.discordapp\.net)\/attachments\/\d+\/\d+\/[^\s<>"']+\.(?:png|jpg|jpeg|gif|bmp|svg|webp)(?:\?[^\s<>"']*)?)/gi,
      (match, url) => {
        const cleanUrl = url.replace(/([.,;!?)]|&)+$/, '');
        return `<img src="${cleanUrl}" alt="Discord Image" style="max-width:100%; height:auto; display:block; cursor:pointer;">`;
      }
    );
  }

  // Images génériques (URLs se terminant par des extensions d'image)
  const imageRegex = /(https?:\/\/[^\s<>"']+\.(?:png|jpg|jpeg|gif|bmp|svg|webp)(?:\?[^\s<>"']*)?)/gi;

  if (imageRegex.test(content) && !content.includes('<img')) {
    content = content.replace(imageRegex, (match, url) => {
      const cleanUrl = url.replace(/([.,;!?)]|&)+$/, '');
      return `<img src="${cleanUrl}" alt="Image" style="max-width:100%; height:auto; display:block; cursor:pointer;">`;
    });
  }


	// ==========================================
	// AUDIO / VIDÉO DIRECTS
	// ==========================================

	// Fichiers vidéo directs (MP4, WEBM, OGV)
	content = content.replace(
		/(https?:\/\/[^\s]+\.(?:mp4|webm|ogv|MP4|WEBM|OGV))(?:[^\s<]*)?/g,
		(match, url) => {
			const cleanUrl = url.replace(/[.,;!?)]$/, '');
			return `<div style="${EMBED_STYLES.container}"><video controls preload="metadata" style="${EMBED_STYLES.video}"><source src="${cleanUrl}" type="video/mp4">Votre navigateur ne supporte pas la lecture de vidéos.</video></div>`;
		}
	);

	// Fichiers audio directs (MP3, OGG, WAV, FLAC)
	content = content.replace(
		/(https?:\/\/[^\s]+\.(?:mp3|ogg|wav|flac|MP3|OGG|WAV|FLAC))(?:[^\s<]*)?/g,
		(match, url) => {
			const cleanUrl = url.replace(/[.,;!?)]$/, '');
			const mimeType = cleanUrl.toLowerCase().endsWith('.mp3') ? 'audio/mpeg' : 'audio/ogg';
			return `<audio controls style="width: 100%; max-width: 720px; display: block;"><source src="${cleanUrl}" type="${mimeType}">Votre navigateur ne supporte pas la lecture audio.</audio>`;
		}
	);

	// ==========================================
	// DÉTECTION DES TYPES DE CONTENU
	// ==========================================
	const textHasVideo = content.includes('<video');
	const textHasImage = content.includes('<img');
	const textHasLink = content.includes('<link');
	const textHasSound = content.includes('<audio');
	const textHasYoutubeVideo = content.includes('youtube.com/embed');
	const textHasEmbed = content.includes('<iframe') || textHasVideo;
	const textHasPlayerLink = playerLinkMatches.length > 0;

	const htmlTagRegex = /<.+?>/;
	const textHasRawContent = !htmlTagRegex.test(content);

	// Trim le contenu s'il contient du HTML
	if (!textHasRawContent) {
		content = content.trim();
	}

	return {
		textHasRawContent,
		textHasVideo,
		textHasImage,
		textHasLink,
		textHasSound,
		textHasYoutubeVideo,
		textHasEmbed,
		textHasPlayerLink,
		playerLinkMatches,
		content,
		originalContent,
	};
}

export default formatHtmlContent;